/**
 * GrayWallUpgradeCard — INVEST-INVESTOR-GRAYWALL v0.01 (2026-05-29)
 *
 * Rendered in place of an investor-only INVEST page's main content for
 * Pro-tier users. Free-tier users are redirected to /pricing before
 * reaching the page; Agency + Enterprise see the live page normally.
 *
 * Voice: generous, not begging. "This is part of Agency — you'll need it
 * once you start managing multiple clients or running an investor
 * practice. For now, here's what's inside: [3 lines]. [Upgrade →]"
 */
import { useNavigate } from "react-router-dom";
import { UpgradeResume } from "@/components/billing/UpgradeMoment";
import { openUpgradeMoment } from "@/lib/upgradeMoment";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Lock, ArrowRight, ExternalLink } from "lucide-react";
import { PLANS } from "@/lib/planPricing";

interface Props {
  featureName: string;
  description: string;   // 3 lines max — what's inside if you upgrade
  bullets?: string[];    // optional concrete items the user would see
  upgradeUrl?: string;
}

export function GrayWallUpgradeCard({
  featureName,
  description,
  bullets,
  upgradeUrl = "/pricing#agency-tier",
}: Props) {
  const navigate = useNavigate();
  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <Card className="p-6 border-amber-500/30 bg-gradient-to-br from-amber-500/5 via-background to-background">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
            <Lock className="h-4 w-4 text-amber-500" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold">{featureName} is included in Agency</h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              Investor-mode features unlock at the Agency tier.
            </p>
          </div>
        </div>
        <p className="text-[13px] leading-relaxed mb-4">{description}</p>
        {bullets && bullets.length > 0 && (
          <ul className="text-[12px] text-muted-foreground space-y-1.5 mb-5 list-disc pl-5">
            {bullets.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          {/* cc_tasks row 82: checkout in a new tab, then this page reloads unlocked. */}
          <UpgradeResume action="reload" onResume={() => window.location.reload()} />
          <Button
            title="Upgrade in a new tab. This page unlocks by itself when your plan is active."
            onClick={() => openUpgradeMoment({ limitType: "plan", attempting: `Open ${featureName}`, need: { plan: "agency", reason: `${featureName} is included in ${PLANS.agency.name}.` }, resume: { action: "reload" } })}
          >
            Upgrade to {PLANS.agency.name} — {PLANS.agency.monthlyPrice}/mo <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
          <Button variant="outline" title="Compare every plan. Opens in a new tab." onClick={() => window.open("https://mentionfox.com/pricing", "_blank", "noopener")}>
            See all Agency features <ExternalLink className="h-3 w-3 ml-1" />
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-4 italic">
          You'll need it once you start managing multiple clients or running an investor practice. For now, browse — the feature names will be here when you decide.
        </p>
      </Card>
    </div>
  );
}
